import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { stageViewportAtom } from '@/store/boardAtoms';
/**
 * Хук для работы с единым стейтом viewport (zoom, position, size)
 * Единая точка входа для всех операций с координатами вида, зумом и размером стейджа
 *
 * @returns Объект с текущим состоянием viewport и методами для его обновления
 * @example
 * ```tsx
 * const { viewport, setZoom, setPosition } = useStageViewport();
 * setZoom(1.5); // Установить зум в 1.5
 * setPosition({ x: 100, y: 200 }); // Переместить в точку (100, 200)
 * ```
 */
export const useStageViewport = () => {
    const [viewport, setViewport] = useAtom(stageViewportAtom);
    /**
     * Обновляет уровень зума (масштаб) viewport
     *
     * @param zoom - Новое значение зума или функция для вычисления нового значения на основе предыдущего
     * @example
     * ```tsx
     * setZoom(1.5); // Установить зум в 1.5
     * setZoom(prev => prev * 1.1); // Увеличить зум на 10%
     * setZoom(prev => prev / 1.1); // Уменьшить зум на 10%
     * ```
     */
    const setZoom = useCallback((zoom) => {
        setViewport((prev) => {
            const newZoom = typeof zoom === 'function' ? zoom(prev.zoom) : zoom;
            return Object.assign(Object.assign({}, prev), { zoom: newZoom });
        });
    }, [setViewport]);
    /**
     * Обновляет позицию (координаты) viewport на стейдже
     *
     * @param position - Новые координаты { x, y } или функция для вычисления новых координат на основе предыдущих
     * @example
     * ```tsx
     * setPosition({ x: 100, y: 200 }); // Переместить в точку (100, 200)
     * setPosition(prev => ({ x: prev.x + 10, y: prev.y })); // Сдвинуть на 10px вправо
     * setPosition(prev => ({ x: window.innerWidth / 2, y: window.innerHeight / 2 })); // Центрировать
     * ```
     */
    const setPosition = useCallback((position) => {
        setViewport((prev) => {
            const newPosition = typeof position === 'function' ? position(prev.position) : position;
            return Object.assign(Object.assign({}, prev), { position: newPosition });
        });
    }, [setViewport]);
    /**
     * Обновляет размер (ширина и высота) viewport
     * Обычно используется при изменении размера окна браузера
     *
     * @param size - Новый размер { width, height } или функция для вычисления нового размера на основе предыдущего
     * @example
     * ```tsx
     * setSize({ width: 1920, height: 1080 }); // Установить размер
     * setSize(prev => ({ width: window.innerWidth, height: window.innerHeight })); // Синхронизировать с размером окна
     * ```
     */
    const setSize = useCallback((size) => {
        setViewport((prev) => {
            const newSize = typeof size === 'function' ? size(prev.size) : size;
            return Object.assign(Object.assign({}, prev), { size: newSize });
        });
    }, [setViewport]);
    /**
     * Обновляет несколько полей viewport одновременно
     * Полезно когда нужно обновить zoom, position и size за один раз
     *
     * @param updates - Частичный объект с обновлениями или функция, возвращающая частичный объект
     * @example
     * ```tsx
     * // Обновить несколько полей одновременно
     * updateViewport({ zoom: 1.5, position: { x: 100, y: 200 } });
     *
     * // Использовать функцию для вычисления обновлений
     * updateViewport(prev => ({
     *   zoom: prev.zoom * 1.1,
     *   size: { width: window.innerWidth, height: window.innerHeight }
     * }));
     * ```
     */
    const updateViewport = useCallback((updates) => {
        setViewport((prev) => {
            const updatesObj = typeof updates === 'function' ? updates(prev) : updates;
            return Object.assign(Object.assign({}, prev), updatesObj);
        });
    }, [setViewport]);
    return {
        /** Полный объект viewport с полями zoom, position, size */
        viewport,
        /** Текущий уровень зума (масштаб) */
        zoom: viewport.zoom,
        /** Текущая позиция viewport { x, y } */
        position: viewport.position,
        /** Текущий размер viewport { width, height } */
        size: viewport.size,
        /** Функция для обновления зума */
        setZoom,
        /** Функция для обновления позиции */
        setPosition,
        /** Функция для обновления размера */
        setSize,
        /** Функция для обновления нескольких полей одновременно */
        updateViewport,
        /** Прямой setter атома (для продвинутых случаев использования) */
        setViewport,
    };
};
